import type { UseQueryOptions, QueryKey } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { briefQuery } from "../../briefs/queries/briefQuery";
import { IBrief } from "../../briefs/types/IBrief";
import { useApi } from "../../SDKProvider";
import { unwrapAPIResult } from "../../utils/api";
import { getResourceUuid } from "../../utils/getResourceUuid";
import { ProfessionalSituationBrief } from "../types/IProfessionalSituationResource";

export type IProfessionalSituationBriefsQueryParameters = {
    briefs?: ProfessionalSituationBrief[];
};

export type IProfessionalSituationBriefsQueryResult = IBrief[];

/**
 * Custom hook to fetch multiple briefs in parallel
 */
export const useProfessionalSituationBriefsQuery = (
    { briefs }: IProfessionalSituationBriefsQueryParameters,
    config?: UseQueryOptions<IProfessionalSituationBriefsQueryResult>,
) => {
    const api = useApi();
    const briefIds = useMemo(() => briefs?.map(brief => getResourceUuid(brief)) ?? [], [briefs]);

    const queryKey: QueryKey = useMemo(() => ["professional-situation-briefs", briefIds], [briefIds]);

    const queryFn = useCallback(async (): Promise<IProfessionalSituationBriefsQueryResult> => {
        if (briefIds.length === 0) {
            return [];
        }

        const briefRequests = briefIds.map(async briefId => {
            try {
                const requestConfig = briefQuery({ brief: briefId });

                if (!requestConfig) {
                    return null;
                }

                const data = await unwrapAPIResult(api.request<IBrief>(requestConfig));
                return data;
            } catch {
                // Return null instead of throwing to allow other requests to continue
                return null;
            }
        });

        const allBriefs = await Promise.all(briefRequests);

        // Filter out failed requests (null values)
        return allBriefs.filter((brief): brief is IBrief => brief !== null);
    }, [briefIds, api]);

    return useQuery({
        queryKey,
        queryFn,
        enabled: briefIds.length > 0,
        ...config,
    });
};
